import { useState, useEffect, useId, type ReactNode } from 'react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import type { ComplianceViolation, PolicyCriticality, DiagnosticsData, PolicyCheckDiagnostics } from '@/types'
import { getWorkloadForProduct } from '@/types'
import { refMatchesFramework, FRAMEWORK_PATTERNS } from '@/lib/frameworkUtils'
import { describeOpenAge, formatShortDate, parseLocalDate } from '@/lib/compliance-format'
import { pickWaiverLifecycleChip } from '@/lib/waiver-lifecycle-chip'
import { parseFixCommand } from '@/lib/fix-command'
import ScoreDeltaChip, { NOISE_THRESHOLD } from '@/components/Dashboard/v2/ScoreDeltaChip'
import FixCommandBlock from '@/components/Fix/FixCommandBlock'
import { Button, ButtonLink } from '@/components/carbon/Button'
import {
  IconCheck,
  IconChevronRight,
  IconClose,
  IconCopy,
  IconExternal,
  IconInfo,
  IconWaiver,
  IconWarning,
} from '@/components/carbon/icons'
import { ButtonSet, RowDetail, RowDetailSection } from '@/components/carbon/RowDetail'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { Tag } from '@/components/carbon/Tag'

// Helper to find the framework reference matching the current context
function getContextualFrameworkRef(refs: string[], frameworkCode?: string): string | null {
  if (!frameworkCode || refs.length === 0) return refs[0] ?? null

  const matchingRef = refs.find(ref => refMatchesFramework(ref, frameworkCode))

  // Only return matching ref for the current framework - no fallback to unrelated refs
  return matchingRef ?? null
}

// Filter framework refs to only those matching the current framework context
function getFrameworkSpecificRefs(refs: string[], frameworkCode?: string): string[] {
  if (!frameworkCode) return refs
  return refs.filter(ref => refMatchesFramework(ref, frameworkCode))
}

// Count distinct frameworks a set of refs maps to (cross-framework view only).
function countDistinctFrameworks(refs: string[]): number {
  const frameworkCodes = Object.keys(FRAMEWORK_PATTERNS)
  const matched = new Set<string>()
  for (const ref of refs) {
    for (const code of frameworkCodes) {
      if (refMatchesFramework(ref, code)) {
        matched.add(code)
        break
      }
    }
  }
  return matched.size
}

/** Display names for the framework codes a reference can map to. */
const FRAMEWORK_DISPLAY: Record<string, string> = {
  SCUBA: 'CISA SCuBA',
  NIST_800_53: 'NIST SP 800-53',
  NIST_CSF: 'NIST CSF',
  MCSB: 'Microsoft MCSB',
  EIDSCA: 'EIDSCA',
  MAESTER: 'Maester',
  CIS: 'CIS',
}

/** The framework code a reference belongs to ("MCSB DP-6" → "MCSB"), or null. */
function frameworkCodeOfRef(ref: string): string | null {
  for (const code of Object.keys(FRAMEWORK_PATTERNS)) {
    if (refMatchesFramework(ref, code)) return code
  }
  return null
}

// Derive the policy type label for policyAnalysis diagnostics
function getPolicyTypeLabel(violation: ComplianceViolation): string | null {
  const product = (violation.product || '').toLowerCase()
  if (product === 'entraid' || product === 'azuread') return 'Conditional Access'
  if (product === 'sharepoint') return 'Conditional Access'
  return null
}

// Format diagnostic values for display
function formatValue(val: unknown): string {
  if (val === true) return 'True'
  if (val === false) return 'False'
  if (val === null || val === undefined) return 'Not set'
  return String(val)
}

/** Parse the diagnostics JSON a violation carries, or null. */
function parseDiagnostics(violation: ComplianceViolation): DiagnosticsData | null {
  if (!violation.diagnostics) return null
  try {
    const parsed = JSON.parse(violation.diagnostics)
    // Backward compat: if no type field but has policyAnalysis, treat as CA-style
    if (!parsed.type && parsed.policyAnalysis) {
      return { ...parsed, type: 'policyAnalysis' } as PolicyCheckDiagnostics
    }
    return parsed as DiagnosticsData
  } catch {
    return null
  }
}

/**
 * The admin center a workload's settings live in (§7 X11, B13). The link is
 * the center's landing page: Guardian links the exact page only when the
 * policy carries a `remediationUrl`.
 */
const ADMIN_CENTER: Record<ReturnType<typeof getWorkloadForProduct>, { name: string; href: string }> = {
  Identity: { name: 'Microsoft Entra', href: 'https://entra.microsoft.com' },
  Exchange: { name: 'Exchange admin center', href: 'https://admin.exchange.microsoft.com' },
  Teams: { name: 'Teams admin center', href: 'https://admin.teams.microsoft.com' },
  SharePoint: { name: 'SharePoint admin center', href: 'https://admin.microsoft.com/sharepoint' },
  Defender: { name: 'Microsoft Defender', href: 'https://security.microsoft.com' },
  Other: { name: 'Microsoft 365 admin center', href: 'https://admin.microsoft.com' },
}

/**
 * Where the row's fix link goes and what it says: the policy's own
 * `remediationUrl` when present (Microsoft Learn reads as "Full instructions
 * on Microsoft Learn"), else the workload's admin-center landing page.
 */
function violationFixLink(violation: ComplianceViolation): { label: string; href: string } {
  const center = ADMIN_CENTER[getWorkloadForProduct(violation.product || violation.category)]
  const url = violation.remediationUrl?.trim()
  if (url) {
    let host = ''
    try {
      host = new URL(url).hostname.toLowerCase()
    } catch {
      host = ''
    }
    if (host.endsWith('learn.microsoft.com')) return { label: 'Full instructions on Microsoft Learn', href: url }
    return { label: `Open in ${center.name}`, href: url }
  }
  return { label: `Open in ${center.name}`, href: center.href }
}

/**
 * Triage presentation: the severity (or lifecycle state) said ONCE, as glyph
 * plus word. Lifecycle states (Passing / Waived / No data) win over the
 * criticality tier because they change what the row asks of the user. Only
 * critical takes a hue (Coral, via SeverityGlyph); the lifecycle states are
 * neutral with a check, waiver or info glyph (D12).
 */
export function ViolationStatusGlyph({ violation }: { violation: Pick<ComplianceViolation, 'status' | 'criticality'> }) {
  const lifecycle = LIFECYCLE[violation.status as keyof typeof LIFECYCLE]
  if (lifecycle) {
    const Icon = lifecycle.icon
    return (
      <span className="inline-flex items-center gap-2 whitespace-nowrap text-text-secondary">
        <Icon className="text-[color:var(--icon-secondary)]" />
        {lifecycle.word}
      </span>
    )
  }
  return (
    <span title={getCriticalityTooltip(violation.criticality)}>
      <SeverityGlyph level={severityFromApi(violation.criticality)} />
    </span>
  )
}

const LIFECYCLE = {
  Pass: { word: 'Passing', icon: IconCheck },
  Resolved: { word: 'Resolved', icon: IconCheck },
  Waived: { word: 'Waived', icon: IconWaiver },
  DataUnavailable: { word: 'No data', icon: IconInfo },
} as const

const CRITICALITY_WORD: Record<string, string> = { SHALL: 'Critical', SHOULD: 'High', MAY: 'Medium' }

// Tooltip frames the criticality tier conversationally and bridges once to
// the points tag so users don't read the two signals as the same axis. Score
// lift is driven by the policy's framework weight, not its tier.
function getCriticalityTooltip(criticality: PolicyCriticality): string {
  switch (criticality) {
    case 'SHALL':
      return "Critical control. Must-fix per the frameworks. The points tag on this row is separate: it's the score lift you'd get from closing this."
    case 'SHOULD':
      return "Recommended by the frameworks. The points tag on this row shows the score lift you'd get from closing this."
    case 'MAY':
    default:
      return "Optional or aspirational. The points tag on this row shows the score lift you'd get from closing this."
  }
}

/** Active waiver type, in customer-facing words. */
function waiverTypeLabelOf(violation: ComplianceViolation): string | null {
  if (!violation.waiver) return null
  if (violation.waiver.waiverType === 'Permanent') return 'Alternative mitigation'
  if (violation.waiver.waiverType === 'Temporary') return 'Temporary exception'
  return 'Not applicable'
}

/**
 * The metadata line under a row's title: rule code, framework reference (or
 * category and product), open age, waiver and lifecycle tags, and the
 * cross-framework count. Shared by the triage row and the table's item cell.
 */
export function ViolationMeta({
  violation,
  frameworkCode,
  compact = false,
}: {
  violation: ComplianceViolation
  frameworkCode?: string
  /** The table's item sub-line: workload and rule code, then the state tags (the table has its own date and framework columns). */
  compact?: boolean
}) {
  const frameworkRefs = violation.frameworkReferences || []
  const contextualRef = getContextualFrameworkRef(frameworkRefs, frameworkCode)
  const contextualRefs = getFrameworkSpecificRefs(frameworkRefs, frameworkCode)
  const diagnostics = parseDiagnostics(violation)
  const lifecycleChip = pickWaiverLifecycleChip({
    status: violation.status,
    waiver: violation.waiver
      ? {
          waiverType: violation.waiver.waiverType,
          expiresAt: violation.waiver.expiresAt,
          isExpired: violation.waiver.isExpired,
        }
      : null,
    recentLapsedAt: violation.recentLapsedAt,
  })
  const openAge = violation.status === 'Open' ? describeOpenAge(violation.firstDetectedAt ?? violation.detectedAt) : ''
  const waiverTypeLabel = waiverTypeLabelOf(violation)

  // "+N more controls" summary for the metadata line (full list lives in the detail).
  const extraControlsLabel = (() => {
    if (frameworkCode) {
      const extra = contextualRefs.length - 1
      if (extra <= 0) return null
      return `+${extra} more ${extra === 1 ? 'control' : 'controls'}`
    }
    if (frameworkRefs.length <= 1) return null
    const frameworkCount = countDistinctFrameworks(frameworkRefs)
    const controlsLabel = `${frameworkRefs.length} controls`
    return frameworkCount > 1 ? `${frameworkCount} frameworks · ${controlsLabel}` : controlsLabel
  })()

  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-helper text-text-helper">
      {compact && <span>{getWorkloadForProduct(violation.product || violation.category)}</span>}
      <span className="break-all font-mono" title="Policy ID">
        {violation.policyCode}
      </span>
      {compact ? null : contextualRef ? (
        <span title={`Framework reference: ${contextualRef}`}>{contextualRef}</span>
      ) : (
        <span>
          {violation.category} · {violation.product}
        </span>
      )}
      {!compact && openAge && <span className="tabular-nums">{openAge}</span>}
      {violation.status === 'Waived' && waiverTypeLabel && (
        <Tag size="sm">
          {waiverTypeLabel}
          {violation.waiver?.waiverType === 'Temporary' && violation.waiver.expiresAt && !violation.waiver.isExpired
            ? ` · expires ${formatShortDate(violation.waiver.expiresAt)}`
            : ''}
        </Tag>
      )}
      {lifecycleChip && (
        <Tag size="sm" kind={lifecycleChip.kind === 'expired' ? 'high-contrast' : 'default'} title={lifecycleChip.tooltip}>
          {lifecycleChip.label}
        </Tag>
      )}
      {violation.status === 'DataUnavailable' && diagnostics?.type === 'dataUnavailable' && diagnostics.licenseRequired && (
        <Tag size="sm">Needs {diagnostics.licenseRequired.name}</Tag>
      )}
      {!compact && extraControlsLabel && <span>{extraControlsLabel}</span>}
    </span>
  )
}

/** The text "Copy fix steps" hands over: the written steps, then the fix itself. */
function fixStepsText(violation: ComplianceViolation, fixLines: string[]): string {
  return [violation.remediationSteps?.trim(), ...fixLines].filter(Boolean).join('\n\n')
}

function copyFixSteps(text: string) {
  void navigator.clipboard.writeText(text).then(
    () => toast.success('Copied. Review before you run it.'),
    () => toast.error('Could not copy. Select and copy manually.'),
  )
}

export interface ViolationDetailProps {
  violation: ComplianceViolation
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
  /** Heading level of the detail sections. Default h3 (under a table's h2). */
  headingLevel?: 'h3' | 'h4'
}

/**
 * The expanded detail of one finding (DESIGN.md "Data table", mockup 03): three
 * columns that stack below 1100px.
 *   What Guardian found   the message verbatim (X8), the evidence
 *   How to fix it         the written steps and the fix block
 *   Frameworks            the severity, rule and framework references, the
 *                         score lift and the waiver on record
 * then a full-width button set (the fix link, "Copy fix steps", the waiver
 * actions) and a helper line with the detection dates. The DataTable row and
 * the standalone triage row both render it.
 */
export function ViolationDetail({ violation, onAddWaiver, onEditWaiver, onDeleteWaiver, headingLevel = 'h3' }: ViolationDetailProps) {
  const frameworkRefs = violation.frameworkReferences || []
  const diagnostics = parseDiagnostics(violation)
  const waiverTypeLabel = waiverTypeLabelOf(violation)
  const level = severityFromApi(violation.criticality)

  const gsImpactLabel =
    violation.status !== 'Pass' && violation.guardianScoreDelta != null && violation.guardianScoreDelta > NOISE_THRESHOLD
      ? `+${violation.guardianScoreDelta.toFixed(1)}`
      : null

  // Rego-authored fix for the "Run this" block (cheap parse, ≤500 chars).
  // Guarded on parsed segments (not raw truthiness) so a whitespace-only value
  // can't open a bare "How to fix it" section, and suppressed on data-unavailable
  // rows: telling the user to run a fix for a control Guardian couldn't even
  // evaluate is contradictory advice. Both signals are checked: status covers
  // rows whose diagnostics JSON is missing, legacy, or unparsable.
  const fix = parseFixCommand(violation.powerShellEquivalent)
  const showFix = fix.segments.length > 0 && violation.status !== 'DataUnavailable' && diagnostics?.type !== 'dataUnavailable'
  const fixHasCommand = fix.commands.length > 0
  const hasFixSection = !!violation.remediationSteps || showFix || diagnostics?.type === 'dataUnavailable'
  const stepsText = fixStepsText(violation, showFix ? (fixHasCommand ? fix.commands : fix.prose) : [])

  const found = (
    <RowDetailSection title="What Guardian found" as={headingLevel}>
      <p className="m-0 max-w-[72ch]">{violation.message}</p>
      {violation.details && <p className="m-0 mt-2 max-w-[72ch]">{violation.details}</p>}

      {/* Setting checks — expected vs actual, one claim per row */}
      {diagnostics?.type === 'settingChecks' && diagnostics.settingChecks.length > 0 && (
        <ul className="m-0 mt-3 list-none border-t border-line-subtle p-0">
          {diagnostics.settingChecks.map((check) => {
            // A long value (recommendation sentence, URL) in the side-by-side
            // columns squeezes the label to zero and the texts collide; stack those rows.
            const stacked = formatValue(check.expected).length > 28 || formatValue(check.actual).length > 28
            return (
              <li
                key={check.setting}
                data-layout={stacked ? 'stacked' : 'inline'}
                className={clsx(
                  'grid grid-cols-[16px_minmax(0,1fr)] items-start gap-x-3 gap-y-1 border-b border-line-subtle py-2 text-body',
                  !stacked && 'c-md:grid-cols-[16px_minmax(0,1fr)_auto_auto]',
                )}
              >
                {check.passed ? (
                  <IconCheck className="mt-0.5 text-[color:var(--icon-secondary)]" title="Passing" />
                ) : (
                  <IconClose className="mt-0.5 text-text-primary" title="Failing" />
                )}
                <span className="min-w-0 text-text-secondary">
                  {check.label}
                  {check.context && <span className="ml-1.5 text-text-helper">({check.context})</span>}
                </span>
                <span className={clsx('col-start-2 text-helper text-text-secondary', stacked ? 'break-words' : 'c-md:col-start-3 c-md:whitespace-nowrap')}>
                  <span className="mr-1.5 text-text-helper">Expected</span>
                  {formatValue(check.expected)}
                </span>
                <span
                  className={clsx(
                    'col-start-2 text-helper',
                    check.passed ? 'text-text-secondary' : 'font-semibold text-text-primary',
                    stacked ? 'break-words' : 'c-md:col-start-4 c-md:whitespace-nowrap',
                  )}
                >
                  <span className="mr-1.5 font-normal text-text-helper">Actual</span>
                  {formatValue(check.actual)}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      {/* Policy analysis — per-policy check breakdown */}
      {diagnostics?.type === 'policyAnalysis' && diagnostics.policyAnalysis.length > 0 && (() => {
        const policyTypeLabel = getPolicyTypeLabel(violation)
        return (
          <div className="mt-3">
            <p className="m-0 text-helper text-text-helper">
              {diagnostics.totalEnabledPolicies} enabled {policyTypeLabel ? `${policyTypeLabel} ` : ''}
              {diagnostics.totalEnabledPolicies === 1 ? 'policy' : 'policies'} analyzed
            </p>
            <ul className="m-0 mt-2 list-none border-t border-line-subtle p-0">
              {diagnostics.policyAnalysis.map((policy) => {
                const isNearMiss = policy.passedCount >= policy.totalChecks - 1 && policy.passedCount < policy.totalChecks
                return (
                  <li key={policy.id} className="border-b border-line-subtle py-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate text-text-primary">{policy.name}</span>
                      <span className="flex-none text-helper tabular-nums text-text-secondary">
                        {policy.passedCount}/{policy.totalChecks}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {diagnostics.requiredConditions.map((condition) => {
                        const passed = policy.checks[condition]
                        const label = diagnostics.conditionLabels[condition] ?? condition
                        return (
                          <Tag
                            key={condition}
                            size="sm"
                            kind={passed ? 'default' : 'outline'}
                            icon={passed ? <IconCheck /> : <IconClose />}
                          >
                            <span className="sr-only">{passed ? 'Passing: ' : 'Failing: '}</span>
                            {label}
                          </Tag>
                        )
                      })}
                    </div>
                    {isNearMiss && (
                      <p className="m-0 mt-2 flex items-center gap-1.5 text-helper text-text-secondary">
                        <IconWarning size={12} />
                        One check away. This policy nearly satisfies the control.
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })()}

      {/* Item list — user/resource list diagnostics */}
      {diagnostics?.type === 'itemList' && diagnostics.items.length > 0 && (
        <div className="mt-3">
          <p className="m-0 text-helper text-text-helper">
            {diagnostics.label} · {diagnostics.totalCount} found
          </p>
          <ul className="m-0 mt-2 list-none border-t border-line-subtle p-0">
            {diagnostics.items.slice(0, 10).map((item, i) => (
              <li key={i} className="flex min-w-0 items-center gap-2 border-b border-line-subtle py-1.5">
                <span className="min-w-0 truncate font-mono text-helper text-text-primary">{item.name}</span>
                {item.detail && <span className="text-helper text-text-helper">({item.detail})</span>}
              </li>
            ))}
          </ul>
          {diagnostics.totalCount > 10 && (
            <p className="m-0 mt-1.5 text-helper text-text-helper">and {diagnostics.totalCount - 10} more</p>
          )}
        </div>
      )}

      {/* Data unavailable — why Guardian couldn't evaluate this */}
      {diagnostics?.type === 'dataUnavailable' && (
        <div className="mt-3 bg-layer-1 p-4">
          <p className="m-0 text-text-secondary">{diagnostics.reason}</p>
          <p className="m-0 mt-1 text-helper text-text-helper">
            Data path: <span className="break-all font-mono">{diagnostics.dataPath}</span>
          </p>
          {diagnostics.licenseRequired && (
            <div className="mt-3 border-t border-line-subtle pt-3">
              <p className="m-0 font-semibold text-text-primary">{diagnostics.licenseRequired.name}</p>
              <p className="m-0 mt-0.5 text-helper text-text-secondary">{diagnostics.licenseRequired.description}</p>
              {diagnostics.licenseRequired.infoUrl && (
                <ButtonLink
                  href={diagnostics.licenseRequired.infoUrl}
                  kind="ghost"
                  size="sm"
                  icon={<IconExternal />}
                  className="-ml-3 mt-1"
                >
                  Learn about this license
                </ButtonLink>
              )}
            </div>
          )}
        </div>
      )}

      {/* Affected resource */}
      {violation.affectedResource && (
        <p className="m-0 mt-3 text-helper text-text-helper">
          Affected resource · <span className="break-all font-mono text-text-secondary">{violation.affectedResource}</span>
        </p>
      )}
    </RowDetailSection>
  )

  const howToFix = hasFixSection ? (
    <RowDetailSection title="How to fix it" as={headingLevel}>
      {violation.remediationSteps && <p className="m-0 max-w-[72ch]">{violation.remediationSteps}</p>}
      {/* Run this — the Rego-authored fix, handed over as text. Command strings
          get the copyable mono block; prose-only values (portal paths, DNS
          records) render as a manual step without a copy affordance. */}
      {showFix && (
        <div className={clsx('max-w-[72ch]', violation.remediationSteps && 'mt-3')}>
          <p className="m-0 mb-1.5 text-helper text-text-helper">Run this</p>
          <FixCommandBlock
            fixType={fixHasCommand ? 'PowerShell' : 'Manual'}
            command={(fixHasCommand ? fix.commands : fix.prose).join('\n')}
            notes={fixHasCommand && fix.prose.length > 0 ? fix.prose.join(' ') : undefined}
            aiGenerated={false}
            copyable={fixHasCommand}
          />
        </div>
      )}
      {diagnostics?.type === 'dataUnavailable' && diagnostics.licenseRequired && (
        <p className={clsx('m-0 max-w-[72ch]', violation.remediationSteps && 'mt-2')}>
          If your organization doesn&rsquo;t need this license, waive this finding as not applicable to dismiss it.
        </p>
      )}
      {/* Plain collection failures (no license gate, no remediation steps) still
          get guidance, so the section never renders as a bare heading. */}
      {diagnostics?.type === 'dataUnavailable' && !diagnostics.licenseRequired && !violation.remediationSteps && (
        <p className="m-0 max-w-[72ch]">
          The required data could not be collected. This may be a data collection issue or a service configuration that
          prevents access. It usually clears on the next evaluation.
        </p>
      )}
    </RowDetailSection>
  ) : null

  const frameworks = (
    <RowDetailSection title="Frameworks" as={headingLevel}>
      <div className="flex flex-wrap items-center gap-2">
        {violation.status === 'Open' ? <SeverityGlyph level={level} /> : <ViolationStatusGlyph violation={violation} />}
        <Tag kind="outline" size="sm" title={getCriticalityTooltip(violation.criticality)}>
          {CRITICALITY_WORD[violation.criticality] ?? 'Medium'}
        </Tag>
        <Tag kind="outline" size="sm">
          <span className="font-mono">{violation.policyCode}</span>
        </Tag>
      </div>
      {/* ALL bindings, not just the contextual ones: in a framework-scoped view the
          cross-framework mappings are what tell the user a fix here also moves
          their other frameworks. */}
      {frameworkRefs.length > 0 && (
        <ul className="m-0 mt-3 list-none space-y-1 p-0">
          {frameworkRefs.map((ref, index) => {
            const code = frameworkCodeOfRef(ref)
            const name = code ? FRAMEWORK_DISPLAY[code] : null
            return (
              <li key={index}>
                {name ? (
                  <>
                    <b>{name}</b> · {ref}
                  </>
                ) : (
                  ref
                )}
              </li>
            )
          })}
        </ul>
      )}

      {gsImpactLabel && (
        <p className="m-0 mt-3">
          <b className="tabular-nums">{gsImpactLabel}</b> Guardian Score points when this closes.
        </p>
      )}

      {/* Active waiver — the exception on record */}
      {violation.hasWaiver && violation.waiver && (
        <div className="mt-3 bg-layer-1 p-4">
          <p className="m-0 text-text-primary">&ldquo;{violation.waiver.reason}&rdquo;</p>
          <p className="m-0 mt-2 text-helper text-text-helper">
            {waiverTypeLabel} · {violation.waiver.approvedBy}
            {violation.waiver.expiresAt
              ? // Full locale date WITH year: a 2027 expiry must not read as next month.
                ` · ${violation.waiver.isExpired ? 'expired' : 'expires'} ${
                  parseLocalDate(violation.waiver.expiresAt)?.toLocaleDateString() ?? violation.waiver.expiresAt
                }`
              : ''}
          </p>
          {violation.waiver.isExpired && (
            <p className="m-0 mt-2 text-helper text-text-secondary">
              This waiver has expired. The finding flips back to Open after the overnight sweep, or remove the waiver to
              flip it now.
            </p>
          )}
        </div>
      )}

      {/* Lapsed-exception note. After the overnight sweep flips an expired temporary
          exception back to Open the live waiver block above is gone, but the note
          the user left on that exception is still worth showing. The backend
          populates recentLapsed* only when the row is NOT currently Waived and only
          inside a 30-day window, so this never double-renders with the waiver block. */}
      {!violation.hasWaiver && violation.recentLapsedReason && (
        <div className="mt-3 bg-layer-1 p-4">
          <p className="m-0 text-text-primary">&ldquo;{violation.recentLapsedReason}&rdquo;</p>
          <p className="m-0 mt-2 text-helper text-text-helper">
            Last exception
            {violation.recentLapsedAt ? ` · lapsed ${formatShortDate(violation.recentLapsedAt)}` : ''}
            {violation.recentLapsedBy ? ` · ${violation.recentLapsedBy}` : ''}
          </p>
          <p className="m-0 mt-2 text-helper text-text-secondary">
            The finding is open again. Re-add an exception if you still need one.
          </p>
        </div>
      )}
    </RowDetailSection>
  )

  // Passing rows have nothing to fix, so they get no fix link.
  const fixLink = violation.status !== 'Pass' ? violationFixLink(violation) : null
  // No-data rows are waivable too: the copy tells the user to waive a finding
  // Guardian cannot evaluate, so the button has to be there (#94).
  const canAddWaiver = !violation.hasWaiver && (violation.status === 'Open' || violation.status === 'DataUnavailable') && !!onAddWaiver
  const buttons: ReactNode[] = []
  if (fixLink) {
    buttons.push(
      <ButtonLink key="fix" href={fixLink.href} kind="primary" size="md" icon={<IconExternal />}>
        {fixLink.label}
      </ButtonLink>,
    )
  }
  if (stepsText && violation.status !== 'Pass') {
    buttons.push(
      <Button key="copy" kind="secondary" size="md" icon={<IconCopy />} onClick={() => copyFixSteps(stepsText)}>
        Copy fix steps
      </Button>,
    )
  }
  if (canAddWaiver) {
    buttons.push(
      <Button key="add" kind="ghost" size="md" icon={<IconWaiver />} onClick={() => onAddWaiver?.(violation)}>
        Add waiver
      </Button>,
    )
  }
  if (violation.hasWaiver && onEditWaiver) {
    buttons.push(
      <Button key="edit" kind="ghost" size="md" icon={<IconWaiver />} onClick={() => onEditWaiver(violation)}>
        {violation.waiver?.isExpired ? 'Renew waiver' : 'Edit waiver'}
      </Button>,
    )
  }
  if (violation.hasWaiver && onDeleteWaiver) {
    buttons.push(
      <Button key="remove" kind="ghost" size="md" icon={<IconClose />} onClick={() => onDeleteWaiver(violation)}>
        Remove waiver
      </Button>,
    )
  }

  const actions = (
    <div className="min-w-0 flex-1">
      {buttons.length > 0 && <ButtonSet>{buttons}</ButtonSet>}
      {/* Detection timestamps. "First detected" is when this current open episode
          began (held across rescans, reset only when the policy resolves and
          re-fails); "Last checked" is the most recent scan touch. */}
      <p className={clsx('m-0 text-helper text-text-helper', buttons.length > 0 && 'mt-3')}>
        First detected {formatShortDate(violation.firstDetectedAt ?? violation.detectedAt)}
        <span> · Last checked {formatShortDate(violation.detectedAt)}</span>
        {violation.resolvedAt && <span> · Resolved {formatShortDate(violation.resolvedAt)}</span>}
      </p>
    </div>
  )

  return (
    <RowDetail
      columns={howToFix ? [found, howToFix, frameworks] : [found, frameworks]}
      actions={actions}
    />
  )
}

interface ViolationRowProps {
  violation: ComplianceViolation
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
  frameworkCode?: string  // e.g., "EIDSCA", "SCUBA", "NIST_800_53"
  id?: string
  defaultExpanded?: boolean
}

/**
 * One finding as a Carbon expandable row, for lists outside a DataTable (the
 * severity groups in By standard and By workload).
 *
 * The collapsed row is a whole-surface disclosure button: severity glyph and
 * word, the title first (the rule code is metadata), the points lift tag, one
 * helper line, and a chevron that turns 90° when open. The expanded row sits
 * on layer-02 and holds ViolationDetail (What Guardian found / How to fix it /
 * Frameworks, then the button set).
 *
 * Rows are flat list items (a border-subtle top line): render them inside a
 * container, not as free-standing cards.
 */
export default function ViolationRow({ violation, onAddWaiver, onEditWaiver, onDeleteWaiver, frameworkCode, id, defaultExpanded }: ViolationRowProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false)
  const detailId = useId()

  useEffect(() => {
    if (defaultExpanded) setIsExpanded(true)
  }, [defaultExpanded])

  const muted = violation.status !== 'Open'

  return (
    <div id={id} className="border-t border-line-subtle first:border-t-0">
      {/* Triage row — the whole surface is the disclosure trigger. */}
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? detailId : undefined}
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'group grid w-full grid-cols-[minmax(0,1fr)_16px] items-start gap-x-4 gap-y-1 px-4 py-3 text-left text-body',
          'transition-colors duration-110 ease-carbon c-md:grid-cols-[7rem_minmax(0,1fr)_16px]',
          isExpanded ? 'bg-layer-2' : 'hover:bg-layer-hover',
        )}
      >
        <span className="pt-px">
          <ViolationStatusGlyph violation={violation} />
        </span>

        <span className="col-start-1 row-start-2 min-w-0 c-md:col-start-2 c-md:row-start-1">
          <span className={clsx('mr-2', muted ? 'text-text-secondary' : 'font-semibold text-text-primary')}>
            {violation.policyTitle}
          </span>
          <span className="inline-flex align-middle">
            <ScoreDeltaChip delta={violation.guardianScoreDelta} omitAriaLabel />
          </span>
          <ViolationMeta violation={violation} frameworkCode={frameworkCode} />
        </span>

        <span className="col-start-2 row-start-1 grid h-5 place-items-center text-[color:var(--icon-secondary)] c-md:col-start-3">
          <IconChevronRight
            className={clsx('transition-transform duration-150 ease-carbon', isExpanded && 'rotate-90')}
          />
        </span>
      </button>

      {/* The finding's detail. */}
      {isExpanded && (
        <div
          id={detailId}
          role="region"
          aria-label="Finding detail"
          className="bg-layer-2 px-4 pb-8 pt-2 c-md:pl-[8.5rem]"
        >
          <ViolationDetail
            violation={violation}
            onAddWaiver={onAddWaiver}
            onEditWaiver={onEditWaiver}
            onDeleteWaiver={onDeleteWaiver}
            headingLevel="h4"
          />
        </div>
      )}
    </div>
  )
}
